import { getDatabase, ref, onValue } from 'firebase/database';

// Interface for season data structure
interface SeasonInfo {
  value: string;
  label: string;
  index: number;
}

/**
 * Fetches the list of available seasons from Firebase
 * Path: /ranked/seasonsList
 * Returns array of seasons with proper formatting for dropdowns
 */
export const fetchAvailableSeasons = (): Promise<SeasonInfo[]> => {
  return new Promise((resolve, reject) => {
    const db = getDatabase();
    const seasonsListRef = ref(db, '/ranked/seasonsList');

    onValue(
      seasonsListRef,
      (snapshot) => {
        try {
          const seasonsList = snapshot.val();
          
          if (!seasonsList || !Array.isArray(seasonsList)) {
            console.warn('No seasons data found or invalid format');
            resolve([]);
            return;
          }

          // Convert the seasons array to dropdown format
          const seasonsForDropdown: SeasonInfo[] = seasonsList
            .map((seasonName: string, index: number) => ({
              value: seasonName,
              label: `${seasonName}${index === seasonsList.length - 1 ? ' (Current)' : ''}`,
              index: index
            }))
            .reverse(); // Reverse to show most recent seasons first

          console.log('Fetched seasons from Firebase:', seasonsForDropdown);
          resolve(seasonsForDropdown);
        } catch (error) {
          console.error('Error processing seasons data:', error);
          reject(error);
        }
      },
      (error) => {
        console.error('Error fetching seasons from Firebase:', error);
        reject(error);
      }
    );
  });
};

/**
 * Fetches the current season index from Firebase
 * Path: /ranked/currentSeasonIndex
 */
export const fetchCurrentSeasonIndex = (): Promise<number> => {
  return new Promise((resolve, reject) => {
    const db = getDatabase();
    const seasonIndexRef = ref(db, '/ranked/currentSeasonIndex');

    onValue(
      seasonIndexRef,
      (snapshot) => {
        const seasonIndex = snapshot.val();
        if (typeof seasonIndex === 'number') {
          resolve(seasonIndex);
        } else {
          reject(new Error('Invalid season index format'));
        }
      },
      reject,
      { onlyOnce: true }
    );
  });
};

/**
 * Fetches season data for a specific season and optional player limit
 * This simulates the query structure from your template
 * Path: /ranked/seasonData/{season}/players/
 */
export const fetchSeasonalResults = async (season: string, playerLimit?: number): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const db = getDatabase();
    const playersRef = ref(db, `/ranked/seasonData/${season}/players/`);

    onValue(
      playersRef,
      (snapshot) => {
        try {
          const playersData = snapshot.val();
          
          if (!playersData) {
            console.log(`No player data found for season ${season}`);
            resolve([]);
            return;
          }

          // Convert Firebase object to array format matching your template
          const players = Object.entries(playersData).map(([pid, playerData]: [string, any]) => ({
            pid,
            name: playerData.name || 'Unknown Player',
            rating: playerData.rating || 0,
            gamesCount: playerData.gamesCount || 0,
            division: playerData.division || 'Open',
            points: playerData.points || 0,
          }));

          // Sort by rating (highest first) - matches your template logic
          players.sort((a, b) => b.rating - a.rating);

          // Apply player limit if specified
          const filteredPlayers = playerLimit ? players.slice(0, playerLimit) : players;

          console.log(`Fetched ${filteredPlayers.length} players for ${season}${playerLimit ? ` (limited to top ${playerLimit})` : ''}`);
          resolve(filteredPlayers);
        } catch (error) {
          console.error(`Error processing season data for ${season}:`, error);
          reject(error);
        }
      },
      (error) => {
        console.error(`Error fetching season data for ${season}:`, error);
        reject(error);
      },
      { onlyOnce: true }
    );
  });
};

/**
 * Utility function to validate if a season exists in the available seasons list
 */
export const validateSeason = async (season: string): Promise<boolean> => {
  try {
    const availableSeasons = await fetchAvailableSeasons();
    return availableSeasons.some(s => s.value === season);
  } catch (error) {
    console.error('Error validating season:', error);
    return false;
  }
};

/**
 * Get default season (current season) from Firebase
 */
export const getDefaultSeason = async (): Promise<string> => {
  try {
    const [seasons, currentIndex] = await Promise.all([
      fetchAvailableSeasons(),
      fetchCurrentSeasonIndex()
    ]);
    
    if (seasons.length > 0 && currentIndex >= 0 && currentIndex < seasons.length) {
      // Find season by index (remember seasons are reversed in fetchAvailableSeasons)
      const currentSeason = seasons.find(s => s.index === currentIndex);
      return currentSeason ? currentSeason.value : seasons[0].value;
    }
    
    return seasons[0]?.value || 'Season15';
  } catch (error) {
    console.error('Error getting default season:', error);
    return 'Season15'; // Fallback
  }
};